import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Cpu,
  GitBranch,
  ShieldCheck,
  Factory,
  Layers,
  CircuitBoard,
  Workflow,
  ListChecks,
  Activity,
  Thermometer,
  Search,
  Mail,
  FileText,
  AlertTriangle,
  Network,
  Eye,
  Award,
  ExternalLink,
  Download,
  FileDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';

// === Cinematic Hero SVG ===========================================
// Left: PCB hint · middle-left: BOM evidence panel · center: glowing
// graph core · right: risk heatmap blending into trusted-zone graph ·
// far right: dashboard panels.  No stock imagery, no logos.
const HeroVisual = () => (
  <svg
    viewBox="0 0 1600 540"
    preserveAspectRatio="xMidYMid slice"
    xmlns="http://www.w3.org/2000/svg"
    className="block w-full h-auto"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="tsa-bg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0A1628" />
        <stop offset="100%" stopColor="#0F1A2B" />
      </linearGradient>
      <linearGradient id="tsa-pcb" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#1B6EC2" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#0A1628" stopOpacity="0.1" />
      </linearGradient>
      <radialGradient id="tsa-core" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.95" />
        <stop offset="55%" stopColor="#1B6EC2" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#0A1628" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="tsa-risk" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.85" />
        <stop offset="50%" stopColor="#EF4444" stopOpacity="0.7" />
        <stop offset="100%" stopColor="#1B6EC2" stopOpacity="0.4" />
      </linearGradient>
      <linearGradient id="tsa-trust" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.75" />
        <stop offset="100%" stopColor="#1B6EC2" stopOpacity="0.05" />
      </linearGradient>
    </defs>

    <rect width="1600" height="540" fill="url(#tsa-bg)" />

    {/* faint grid */}
    <g stroke="#1B6EC2" strokeOpacity="0.08" strokeWidth="1">
      {[...Array(17)].map((_, i) => (
        <line key={`v${i}`} x1={i * 100} y1="0" x2={i * 100} y2="540" />
      ))}
      {[...Array(7)].map((_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 90} x2="1600" y2={i * 90} />
      ))}
    </g>

    {/* === LEFT: PCB === */}
    <g transform="translate(40, 90)">
      <rect width="360" height="360" rx="14" fill="url(#tsa-pcb)" stroke="#1B6EC2" strokeOpacity="0.45" />
      {/* trace lines */}
      <g stroke="#00D4FF" strokeOpacity="0.55" strokeWidth="1.5" fill="none">
        <path d="M30 60 H180 V120 H300 V200" />
        <path d="M30 130 H140 V220 H260 V300" />
        <path d="M30 200 H110 V280 H210" />
        <path d="M250 30 V90 H320 V160" />
      </g>
      {/* chips */}
      {[
        [60, 60, 60, 30],   [180, 110, 70, 40],
        [240, 200, 80, 50], [80, 240, 70, 40],
        [200, 270, 60, 40], [40, 130, 50, 30],
      ].map(([x, y, w, h], i) => (
        <g key={i}>
          <rect x={x} y={y} width={w} height={h} fill="#0A1628" stroke="#00D4FF" strokeOpacity="0.6" />
          <rect x={x + 4} y={y + 4} width={w - 8} height={h - 8} fill="#1B6EC2" fillOpacity="0.25" />
        </g>
      ))}
      {/* solder dots */}
      <g fill="#F59E0B">
        {[[40, 40], [180, 60], [320, 80], [60, 220], [240, 280], [320, 240]].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="3" />
        ))}
      </g>
    </g>

    {/* === MIDDLE-LEFT: BOM evidence panel === */}
    <g transform="translate(440, 110)">
      <rect width="280" height="320" rx="10" fill="#0F1A2B" stroke="#00D4FF" strokeOpacity="0.4" />
      <text x="20" y="32" fontFamily="Inter,Arial,sans-serif" fontSize="11" fill="#00D4FF" letterSpacing="2">BOM EVIDENCE</text>
      <line x1="20" y1="46" x2="260" y2="46" stroke="#1B6EC2" strokeOpacity="0.3" />
      {[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const y = 70 + i * 32;
        const widths = [200, 170, 220, 150, 200, 180, 210];
        return (
          <g key={i}>
            <rect x="20" y={y - 12} width="14" height="14" fill="#1B6EC2" fillOpacity="0.4" stroke="#00D4FF" strokeOpacity="0.55" />
            <rect x="44" y={y - 8} width={widths[i]} height="6" fill="#BFDBFE" fillOpacity="0.45" rx="2" />
            <rect x="44" y={y + 2} width={widths[i] * 0.55} height="4" fill="#BFDBFE" fillOpacity="0.25" rx="2" />
          </g>
        );
      })}
    </g>

    {/* === CENTER: glowing data-processing core === */}
    <g transform="translate(800, 270)">
      <circle r="180" fill="url(#tsa-core)" />
      <circle r="62" fill="#0A1628" stroke="#00D4FF" strokeOpacity="0.9" strokeWidth="1.5" />
      <text textAnchor="middle" y="-4" fontFamily="Inter,Arial,sans-serif" fontSize="11" fill="#00D4FF" letterSpacing="3">SALAR</text>
      <text textAnchor="middle" y="14" fontFamily="Inter,Arial,sans-serif" fontSize="9" fill="#93C5FD" letterSpacing="2">DECISION CORE</text>
      {/* radial spokes to graph nodes */}
      {[...Array(10)].map((_, i) => {
        const a = (Math.PI * 2 * i) / 10;
        const x = Math.cos(a) * 150;
        const y = Math.sin(a) * 150;
        return (
          <g key={i}>
            <line x1="0" y1="0" x2={x} y2={y} stroke="#00D4FF" strokeOpacity="0.5" strokeWidth="1" />
            <circle cx={x} cy={y} r="5" fill="#00D4FF" />
          </g>
        );
      })}
    </g>

    {/* === RIGHT: risk heatmap → trusted-zone graph === */}
    <g transform="translate(1060, 110)">
      <rect width="220" height="130" rx="8" fill="url(#tsa-risk)" stroke="#F59E0B" strokeOpacity="0.4" />
      <text x="14" y="22" fontFamily="Inter,Arial,sans-serif" fontSize="10" fill="#FFFFFF" letterSpacing="2">RISK HEATMAP</text>
      {/* heatmap cells */}
      <g>
        {[...Array(40)].map((_, i) => {
          const r = Math.floor(i / 10);
          const c = i % 10;
          const opa = (Math.sin(i * 0.5) + 1) / 2;
          const fill = i % 7 === 0 ? '#EF4444' : i % 5 === 0 ? '#F59E0B' : '#FBBF24';
          return (
            <rect
              key={i}
              x={14 + c * 19}
              y={36 + r * 20}
              width="16"
              height="16"
              fill={fill}
              fillOpacity={0.2 + opa * 0.7}
              rx="2"
            />
          );
        })}
      </g>
    </g>

    <g transform="translate(1060, 270)">
      <rect width="220" height="160" rx="8" fill="url(#tsa-trust)" stroke="#00D4FF" strokeOpacity="0.45" />
      <text x="14" y="22" fontFamily="Inter,Arial,sans-serif" fontSize="10" fill="#00D4FF" letterSpacing="2">TRUSTED-ZONE GRAPH</text>
      {/* graph nodes */}
      {[
        [40, 60], [110, 50], [180, 75], [60, 110], [140, 120], [190, 110],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="5" fill="#00D4FF" />
      ))}
      <g stroke="#00D4FF" strokeOpacity="0.45" strokeWidth="1">
        <line x1="40"  y1="60"  x2="110" y2="50" />
        <line x1="110" y1="50"  x2="180" y2="75" />
        <line x1="40"  y1="60"  x2="60"  y2="110" />
        <line x1="60"  y1="110" x2="140" y2="120" />
        <line x1="140" y1="120" x2="190" y2="110" />
        <line x1="180" y1="75"  x2="190" y2="110" />
      </g>
    </g>

    {/* === FAR RIGHT: dashboard panel cluster === */}
    <g transform="translate(1320, 110)">
      {[
        [0,   0,   '#00D4FF', 'SHIELD'],
        [120, 0,   '#F59E0B', 'TEMP'],
        [0,   100, '#22D3EE', 'WAVE'],
        [120, 100, '#A78BFA', 'CHECK'],
        [0,   200, '#FBBF24', 'SEARCH'],
        [120, 200, '#34D399', 'GRAPH'],
      ].map(([x, y, color, label], i) => (
        <g key={i} transform={`translate(${x}, ${y})`}>
          <rect width="100" height="80" rx="6" fill="#0F1A2B" stroke={color} strokeOpacity="0.55" />
          <rect x="8" y="10" width="50" height="6" fill={color} fillOpacity="0.6" rx="1" />
          <rect x="8" y="22" width="80" height="3" fill="#BFDBFE" fillOpacity="0.3" rx="1" />
          <rect x="8" y="30" width="64" height="3" fill="#BFDBFE" fillOpacity="0.2" rx="1" />
          <rect x="8" y="50" width="84" height="20" fill={color} fillOpacity="0.18" rx="3" />
          <text x="50" y="64" textAnchor="middle" fontFamily="Inter,Arial,sans-serif" fontSize="8" fill={color} letterSpacing="2">{label}</text>
        </g>
      ))}
    </g>

    {/* === BOTTOM: lifecycle icon strip === */}
    <g transform="translate(40, 470)">
      {[
        'EVIDENCE', 'INSPECTION', 'TRUST', 'CHIP', 'THERMAL', 'WAVEFORM', 'SHIELD', 'REPORT',
      ].map((t, i) => (
        <g key={t} transform={`translate(${i * 195}, 0)`}>
          <rect width="180" height="48" rx="8" fill="#1A2744" stroke="#00D4FF" strokeOpacity="0.25" />
          <circle cx="24" cy="24" r="11" fill="#0F1A2B" stroke="#00D4FF" strokeOpacity="0.55" />
          <text x="44" y="29" fontFamily="Inter,Arial,sans-serif" fontSize="10" fill="#BFDBFE" letterSpacing="2">{t}</text>
        </g>
      ))}
    </g>
  </svg>
);

// === Workflow stage data ==========================================
const WORKFLOW_STAGES = [
  { id: 'inputs',    label: 'Evidence Inputs',    desc: 'BOM, PCB data, schematic notes, supplier records, lot history, process logs, test results, and field signals.', Icon: FileText, color: '#00D4FF' },
  { id: 'norm',      label: 'Normalization',      desc: 'Disconnected files and records become structured evidence objects.', Icon: Workflow, color: '#22D3EE' },
  { id: 'graph',     label: 'Dependency Graph',   desc: 'Components, suppliers, packages, lots, process routes, and risk relationships are mapped together.', Icon: GitBranch, color: '#A78BFA' },
  { id: 'scoring',   label: 'Risk Scoring',       desc: 'Design, failure, supply, manufacturing, provenance, and process-control risks are scored together.', Icon: Activity, color: '#F59E0B' },
  { id: 'decisions', label: 'Decision Outputs',   desc: 'Accept, inspect, quarantine, replace, redesign, monitor, or elevate for engineering review.', Icon: ListChecks, color: '#FBBF24' },
  { id: 'report',    label: 'Trace Report',       desc: 'Decision logs, evidence reports, audit-ready summaries, and exportable review artifacts.', Icon: Eye, color: '#34D399' },
];

// === Five-lane data ===============================================
const LANES = [
  { id: 'design',    n: '01', t: 'Design Reliability Analysis',                 d: 'Board-level rule logic, derating checks, layout/manufacturing evidence, and structured review outputs.',                                Icon: CircuitBoard, color: '#1B6EC2' },
  { id: 'failure',   n: '02', t: 'Predictive Failure Analytics',                d: 'Physics-informed and evidence-based risk ranking for assemblies, components, and mission electronics.',                                  Icon: Activity,    color: '#22D3EE' },
  { id: 'supply',    n: '03', t: 'Semiconductor Supply-Chain Risk Intelligence', d: 'Multi-tier supplier graphing, hidden dependency detection, chokepoint scoring, and mitigation logic.',                                   Icon: Network,     color: '#F59E0B' },
  { id: 'mfg',       n: '04', t: 'Closed-Loop Manufacturing Stability',          d: 'Process drift, yield-risk awareness, output-confidence scoring, and stability-linked decision support.',                                Icon: Factory,     color: '#FB923C' },
  { id: 'process',   n: '05', t: 'Public-Safe Process Assurance',                d: 'High-level trusted-process and advanced-manufacturing assurance framing without disclosing sensitive implementation details.',          Icon: Layers,      color: '#A78BFA' },
];

// === Section Tag component ========================================
const SectionTag = ({ children, color = '#00D4FF' }) => (
  <div
    className="text-xs font-bold tracking-widest uppercase mb-3"
    style={{ color }}
  >
    {children}
  </div>
);

const TotalSolutionArchitecture = () => {
  return (
    <div className="min-h-screen" style={{ background: '#0A1628' }} data-testid="total-solution-page">
      <Helmet>
        <title>SALAR Total Solution Architecture | Electronics Reliability &amp; Supply-Chain Intelligence</title>
        <meta
          name="description"
          content="SALAR Technologies LLC provides an integrated assurance architecture for mission-critical electronics: board reliability, predictive failure analytics, semiconductor supply-chain risk, provenance control, manufacturing stability, and decision-trace reporting."
        />
        <meta property="og:title" content="SALAR Total Solution Architecture" />
        <meta
          property="og:description"
          content="From board evidence to mission-ready decisions: reliability, provenance, supply-chain risk, manufacturing stability, and traceable technical decision support."
        />
        <meta property="og:url" content="https://www.salartechnologies.com/total-solution-architecture" />
        <meta property="og:image" content="https://www.salartechnologies.com/img/salar_hero_semiconductor_resilience.webp" />
        <link rel="canonical" href="https://www.salartechnologies.com/total-solution-architecture" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* === HERO === */}
      <section
        className="relative text-white py-12 md:py-16 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #0A1628 0%, #0F1A2B 100%)' }}
        data-testid="tsa-hero"
      >
        {/* Wow-effect circuit-grid overlay (decorative, aria-hidden) */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <svg
            viewBox="0 0 1920 700"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute inset-0 w-full h-full"
            style={{ opacity: 0.4 }}
          >
            <defs>
              <radialGradient id="heroGlow" cx="70%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.18" />
                <stop offset="60%" stopColor="#1B6EC2" stopOpacity="0.06" />
                <stop offset="100%" stopColor="#0A1628" stopOpacity="0" />
              </radialGradient>
              <pattern id="heroGrid" width="80" height="80" patternUnits="userSpaceOnUse">
                <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#1B6EC2" strokeOpacity="0.12" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="1920" height="700" fill="url(#heroGrid)" />
            <rect width="1920" height="700" fill="url(#heroGlow)" />
            {/* Soft glowing nodes */}
            {[
              [240, 160], [520, 230], [860, 120], [1280, 200], [1620, 340],
              [380, 480], [720, 560], [1100, 460], [1480, 540],
            ].map(([cx, cy], i) => (
              <g key={i}>
                <circle cx={cx} cy={cy} r="18" fill="#00D4FF" fillOpacity="0.06" />
                <circle cx={cx} cy={cy} r="3" fill="#00D4FF" fillOpacity="0.55" />
              </g>
            ))}
            {/* Soft connector traces */}
            <g stroke="#00D4FF" strokeOpacity="0.18" strokeWidth="1" fill="none">
              <path d="M240 160 Q400 100 520 230" />
              <path d="M520 230 Q700 180 860 120" />
              <path d="M860 120 Q1080 220 1280 200" />
              <path d="M1280 200 Q1480 280 1620 340" />
              <path d="M380 480 Q540 540 720 560" />
              <path d="M720 560 Q900 480 1100 460" />
              <path d="M1100 460 Q1300 520 1480 540" />
            </g>
          </svg>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-5">
              <Badge variant="outline" className="border-amber-400 text-amber-300 mb-4 tracking-widest text-xs">
                INTEGRATED ASSURANCE ARCHITECTURE
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-4">
                SALAR Total Solution Architecture
              </h1>
              <p className="text-lg md:text-xl font-semibold text-cyan-200 mb-4">
                From board evidence to mission-ready decisions.
              </p>
              <p className="text-base text-blue-100 leading-relaxed mb-3">
                SALAR Technologies LLC connects electronics reliability, semiconductor supply-chain
                intelligence, provenance/counterfeit-risk control, manufacturing-stability analytics, and
                predictive failure reasoning into one integrated decision-support architecture.
              </p>
              <p className="text-sm text-blue-200/85 leading-relaxed mb-6">
                Modern electronics risk is not caused by one isolated problem. A board can fail because of
                design margin, supplier opacity, weak provenance, counterfeit ingress, manufacturing drift,
                thermal stress, unstable replacement paths, or missing decision traceability. SALAR&rsquo;s
                total solution architecture connects those signals into a structured, explainable, and
                auditable workflow.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="mailto:vikrant@salartechnologies.com?subject=SALAR%20Technical%20Brief%20Request" data-testid="tsa-hero-brief-btn">
                  <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
                    <Mail className="mr-2" size={18} />
                    Request Technical Brief
                  </Button>
                </a>
                <Link to="/patent-portfolio">
                  <Button size="lg" variant="outline" className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]" data-testid="tsa-hero-patents-btn">
                    View Patent Portfolio
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              {/* 20-Slide Executive Brief — START HERE download card */}
              <div
                className="rounded-xl border border-amber-400/45 p-5 md:p-6 mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(10,22,40,0.55) 60%, rgba(15,26,43,0.85) 100%)',
                  boxShadow: '0 10px 36px rgba(245,158,11,0.10), 0 4px 18px rgba(0,0,0,0.4)',
                }}
                data-testid="tsa-deck-cta-card"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.55)' }}
                    aria-hidden="true"
                  >
                    <FileDown size={22} className="text-amber-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge
                        variant="outline"
                        className="border-amber-400 text-amber-300 text-[10px] tracking-widest"
                        data-testid="tsa-deck-start-here-badge"
                      >
                        START HERE
                      </Badge>
                      <span className="text-[10px] text-amber-200/80 tracking-widest uppercase">PPTX &middot; 20 SLIDES</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-white leading-snug mb-1.5">
                      20-Slide Executive Brief
                    </h3>
                    <p className="text-sm text-blue-100/95 leading-relaxed mb-1">
                      A 5&ndash;10 minute visual walkthrough of SALAR&rsquo;s total solution architecture,
                      Five-Pillar Health Model, scoring logic, confidence bands, risk drivers, and engagement
                      path.
                    </p>
                    <p className="text-xs text-blue-300/85 leading-relaxed mb-4">
                      Download the concise marketing deck for a fast overview before exploring the full
                      architecture graphic below.
                    </p>
                    <a
                      href="/documents/SALAR_Marketing_20Slide.pptx"
                      download
                      data-testid="tsa-deck-download-btn"
                      aria-label="Download 20-slide SALAR Total Solution Architecture executive brief, PowerPoint deck, 20 slides"
                      onClick={() => {
                        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                          window.gtag('event', 'file_download', {
                            file_name: 'SALAR_Marketing_20Slide',
                            file_extension: 'pptx',
                            link_url: '/documents/SALAR_Marketing_20Slide.pptx',
                          });
                        }
                      }}
                    >
                      <Button
                        className="bg-amber-500 hover:bg-amber-600 text-[#0a1628] font-bold"
                      >
                        <Download className="mr-2" size={16} />
                        Download 20-Slide PPTX
                      </Button>
                    </a>
                    <div className="mt-3 text-[11px] text-blue-300/75 leading-relaxed">
                      PowerPoint deck &middot; 20 slides &middot; Executive / technical overview
                    </div>
                  </div>
                </div>
              </div>

              {/* Thermal Resilience Scenario Brief — companion scenario worked-example */}
              <div
                className="rounded-xl border border-cyan-400/30 p-5 md:p-6 mt-4"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,212,255,0.08) 0%, rgba(10,22,40,0.55) 60%, rgba(15,26,43,0.85) 100%)',
                  boxShadow: '0 10px 36px rgba(0,212,255,0.10), 0 4px 18px rgba(0,0,0,0.4)',
                }}
                data-testid="tsa-thermal-cta-card"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.55)' }}
                    aria-hidden="true"
                  >
                    <Activity size={22} className="text-cyan-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge variant="outline" className="border-cyan-400 text-cyan-200 text-[10px] tracking-widest">
                        SCENARIO BRIEF
                      </Badge>
                      <span className="text-[10px] text-cyan-200/80 tracking-widest uppercase">
                        Worked Example &middot; Public-Safe
                      </span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-white leading-snug mb-1.5">
                      Thermal Resilience Scenario Brief
                    </h3>
                    <p className="text-sm text-blue-100/95 leading-relaxed mb-2">
                      Scenario Brief: Data Center Thermal Resilience &mdash; thermal or power events can
                      start as facility issues but propagate into workload latency, customer disruption,
                      supplier / spares constraints, market continuity risk, and audit questions.
                    </p>
                    <p className="text-xs text-blue-300/85 leading-relaxed mb-4">
                      A generic, public-safe SALAR scenario brief showing how the P1&ndash;P5 assurance
                      model can translate a data-center thermal or power event into evidence, scoring,
                      mitigation sequencing, and a scoped commercial engagement. The brief converts
                      fragmented signals into a scored readiness view, repair heat map, decision trace,
                      and mitigation backlog.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <a
                        href="/documents/SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief.pdf"
                        download
                        data-testid="tsa-thermal-pdf-btn"
                        aria-label="Download Thermal Resilience Scenario Brief, PDF"
                        onClick={() => {
                          if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                            window.gtag('event', 'file_download', {
                              file_name: 'SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief',
                              file_extension: 'pdf',
                              link_url: '/documents/SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief.pdf',
                            });
                          }
                        }}
                      >
                        <Button className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold w-full sm:w-auto">
                          <Download className="mr-2" size={16} />
                          Download Scenario PDF
                        </Button>
                      </a>
                      <a
                        href="/documents/SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief.pptx"
                        download
                        data-testid="tsa-thermal-pptx-btn"
                        aria-label="Download Thermal Resilience Scenario Brief, editable PowerPoint"
                        onClick={() => {
                          if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                            window.gtag('event', 'file_download', {
                              file_name: 'SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief',
                              file_extension: 'pptx',
                              link_url: '/documents/SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief.pptx',
                            });
                          }
                        }}
                      >
                        <Button variant="outline" className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628] font-semibold w-full sm:w-auto">
                          <FileDown className="mr-2" size={16} />
                          Download Editable PPTX
                        </Button>
                      </a>
                    </div>
                    <div className="mt-3 text-[11px] text-blue-300/75 leading-relaxed">
                      Worked example &middot; generic and non-endorsing &middot; companion to the 20-slide framework deck
                    </div>
                  </div>
                </div>
              </div>

              {/* Public-safe vendor-routing note. Do not imply third-party endorsement or approved-vendor status. */}
              <div
                className="mt-4 rounded-lg border border-blue-500/25 p-4"
                style={{ background: 'rgba(15, 26, 43, 0.55)' }}
                data-testid="tsa-vendor-routing-note"
              >
                <div className="text-blue-300/80 text-[10px] font-bold tracking-widest uppercase mb-2">
                  Vendor / Sourcing Route Guardrail
                </div>
                <p className="text-xs text-blue-100/85 leading-relaxed mb-2">
                  For regulated cloud, fintech, trading-platform, or exchange operators, SALAR Technologies
                  LLC uses the customer&rsquo;s official vendor, sourcing, legal, and procurement process.
                  SALAR does not use customer-support or retail-account channels for business-development
                  outreach. Where a company requires approved-vendor onboarding, SALAR will wait for the
                  official sourcing invitation and complete the required vendor, business, tax, and banking
                  forms through the customer-designated portal.
                </p>
                <p className="text-xs text-blue-300/70 leading-relaxed italic">
                  For example, Coinbase&rsquo;s vendor process is invitation-based through its Zip Vendor
                  Portal for approved vendors, with tax documentation and banking information completed
                  only after the vendor is invited through the official channel.
                </p>
              </div>
              {/* Big Picture — Total Solution View */}
              <div
                className="text-cyan-300 text-xs font-bold tracking-widest uppercase mb-2 mt-6"
                data-testid="tsa-big-picture-eyebrow"
              >
                Total Solution View
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight mb-2" data-testid="tsa-big-picture-heading">
                Big Picture
              </h2>
              <p className="text-sm md:text-base text-blue-100/95 leading-relaxed mb-4 max-w-3xl">
                From board evidence to mission-ready decisions, this architecture connects reliability,
                verification evidence, supplier provenance, manufacturing stability, qualification assurance,
                and executive risk interpretation into one traceable decision workflow.
              </p>

              <div
                className="rounded-2xl overflow-hidden border-2 border-cyan-400/30 p-3 md:p-4 transition-transform hover:scale-[1.01] motion-reduce:hover:scale-100"
                style={{
                  boxShadow: '0 28px 72px rgba(0,212,255,0.22), 0 18px 48px rgba(0,0,0,0.5)',
                  background: 'linear-gradient(180deg, #0F1A2B 0%, #0A1628 100%)',
                }}
                role="img"
                aria-label="SALAR Total Solution Architecture big-picture workflow linking evidence, inspection, trust, chip, thermal, waveform, shield, report, branch, and map decision layers."
                data-testid="tsa-hero-visual"
              >
                <div className="rounded-xl overflow-hidden" style={{ background: '#0A1628' }}>
                  <HeroVisual />
                </div>
              </div>
              {/* Signal labels strip — only desktop, decorative legend */}
              <div className="hidden md:flex flex-wrap justify-center gap-2 mt-4" aria-hidden="true">
                {['Evidence', 'Inspection', 'Trust', 'Chip', 'Thermal', 'Waveform', 'Shield', 'Report', 'Branch', 'Map'].map((s) => (
                  <span
                    key={s}
                    className="text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border border-cyan-500/30 text-cyan-200 bg-[#0F1A2B]/60"
                    title={s}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === SECTION 1 — THE PROBLEM === */}
      <section className="py-20" style={{ background: '#0F1A2B' }} data-testid="tsa-problem">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Section 01 &middot; The Problem</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">Fragmented Electronics Decisions</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                Mission electronics risk is rarely caused by one isolated issue. A board can fail because of
                design margin, supplier opacity, weak provenance, counterfeit ingress, manufacturing drift,
                thermal stress, or unstable replacement paths. SALAR is designed to connect these signals
                into a structured, explainable, and auditable workflow.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
              {[
                { Icon: CircuitBoard, t: 'Board Design Fragility',     d: 'Weak margin, layout, derating, thermal behavior, or manufacturing-handling assumptions can create latent field failures.' },
                { Icon: Network,      t: 'Lower-Tier Supply Opacity', d: 'Shared suppliers, OSAT paths, materials, logistics routes, and hidden dependencies can remain invisible until disruption.' },
                { Icon: ShieldCheck,  t: 'Weak Provenance',            d: 'Authorized-source status, lot history, storage, handling, chain-of-custody, and distributor confidence affect trust.' },
                { Icon: Factory,      t: 'Manufacturing Drift',        d: 'Nominal supplier availability does not equal stable or trusted output. Process drift can silently degrade production confidence.' },
                { Icon: AlertTriangle,t: 'Poor Decision Traceability', d: 'Risk reports often lack a clear evidence trail showing why a recommendation was produced and which data drove the result.' },
              ].map(({ Icon, t, d }, i) => (
                <div
                  key={t}
                  data-testid={`tsa-problem-card-${i}`}
                  className="rounded-xl p-5 border border-amber-400/20 transition-transform hover:-translate-y-1 motion-reduce:hover:translate-y-0"
                  style={{
                    background: 'linear-gradient(180deg, rgba(245,158,11,0.06) 0%, rgba(15,26,43,0.6) 100%)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <div className="w-11 h-11 rounded-lg bg-amber-500/15 border border-amber-400/40 flex items-center justify-center mb-3">
                    <Icon className="text-amber-300" size={20} />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2 leading-snug">{t}</h3>
                  <p className="text-xs text-blue-100/90 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === SECTION 2 — FIVE LANES === */}
      <section className="py-20" style={{ background: '#0A1628' }} data-testid="tsa-five-lanes">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Section 02 &middot; Five Lanes. One Decision System.</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">Five capability lanes, one architecture</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                SALAR&rsquo;s public-safe architecture connects five capability lanes into one
                evidence-to-decision framework.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
              {LANES.map((lane) => (
                <div
                  key={lane.id}
                  data-testid={`tsa-lane-${lane.id}`}
                  className="relative rounded-xl p-5 border transition-transform hover:-translate-y-1 motion-reduce:hover:translate-y-0"
                  style={{
                    background: '#1A2744',
                    borderColor: `${lane.color}55`,
                    borderTop: `3px solid ${lane.color}`,
                  }}
                >
                  <div className="text-[10px] font-bold tracking-widest" style={{ color: lane.color }}>
                    LANE {lane.n}
                  </div>
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center my-3"
                    style={{ background: `${lane.color}1a`, border: `1px solid ${lane.color}55` }}
                  >
                    <lane.Icon size={22} style={{ color: lane.color }} />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2 leading-snug">{lane.t}</h3>
                  <p className="text-xs text-blue-100/90 leading-relaxed">{lane.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === SECTION 3 — WORKFLOW === */}
      <section className="py-20" style={{ background: '#0F1A2B' }} data-testid="tsa-workflow">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Section 03 &middot; Workflow</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">Evidence-to-Decision Workflow</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                SALAR converts fragmented technical, supplier, process, and field evidence into ranked
                decisions that can be reviewed, explained, and exported.
              </p>
            </div>

            {/* Pipeline header */}
            <div className="relative rounded-xl p-4 mb-6" style={{ background: '#0A1628', border: '1px solid rgba(0,212,255,0.2)' }}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {WORKFLOW_STAGES.map((s, i) => (
                  <div key={s.id} className="text-center" data-testid={`tsa-workflow-pipe-${s.id}`}>
                    <div
                      className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-2"
                      style={{ background: `${s.color}1a`, border: `1px solid ${s.color}80` }}
                    >
                      <s.Icon size={20} style={{ color: s.color }} />
                    </div>
                    <div className="text-[10px] font-bold tracking-widest" style={{ color: s.color }}>
                      STAGE {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="text-xs text-white font-semibold mt-1 leading-tight">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stage detail cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {WORKFLOW_STAGES.map((s, i) => (
                <div
                  key={s.id}
                  className="rounded-lg p-5"
                  style={{ background: '#1A2744', borderLeft: `3px solid ${s.color}` }}
                >
                  <div className="text-[10px] font-bold tracking-widest mb-1" style={{ color: s.color }}>
                    STAGE {String(i + 1).padStart(2, '0')}
                  </div>
                  <h4 className="text-base font-bold text-white mb-2">{s.label}</h4>
                  <p className="text-xs text-blue-100/90 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === SECTION 4 — DECISION OUTPUTS === */}
      <section className="py-20" style={{ background: '#0A1628' }} data-testid="tsa-outputs">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Section 04 &middot; Decision Outputs</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">Example Decision Outputs</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                The architecture is designed to produce decision artifacts that engineering, sourcing,
                quality, sustainment, and leadership teams can understand.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { id: 'rel',   Icon: ShieldCheck,  c: '#00D4FF', t: 'Reliability Scorecard',          d: 'Design and component-level risk drivers.' },
                { id: 'src',   Icon: Search,       c: '#22D3EE', t: 'Source Confidence Map',          d: 'Supplier, distributor, lot, and provenance confidence.' },
                { id: 'heat',  Icon: Activity,     c: '#F59E0B', t: 'Component Risk Heatmap',         d: 'Component families ranked by exposure.' },
                { id: 'mfg',   Icon: Thermometer,  c: '#FB923C', t: 'Manufacturing Stability Index',  d: 'Process drift and output-confidence concerns.' },
                { id: 'mit',   Icon: ListChecks,   c: '#A78BFA', t: 'Mitigation Queue',                d: 'Risk converted into recommended actions.' },
                { id: 'trace', Icon: FileText,     c: '#34D399', t: 'Decision Trace Report',           d: 'Why a score or recommendation was produced.' },
              ].map((o) => (
                <Card
                  key={o.id}
                  data-testid={`tsa-output-${o.id}`}
                  className="border transition-transform hover:-translate-y-1 motion-reduce:hover:translate-y-0"
                  style={{ background: '#1A2744', borderColor: `${o.c}40` }}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${o.c}18`, border: `1px solid ${o.c}60` }}>
                        <o.Icon size={18} style={{ color: o.c }} />
                      </div>
                      <h3 className="text-base font-bold text-white leading-snug">{o.t}</h3>
                    </div>
                    <p className="text-xs text-blue-100/90 leading-relaxed">{o.d}</p>
                    {/* Mini stylized chart hint per card */}
                    <div className="mt-4 h-8 rounded" style={{ background: `linear-gradient(90deg, ${o.c}55 0%, ${o.c}10 100%)` }} aria-hidden="true" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === SECTION 5 — RISK SCORING LOGIC === */}
      <section className="py-20" style={{ background: '#0F1A2B' }} data-testid="tsa-risk-logic">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <SectionTag>Section 05 &middot; Risk Logic</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">Risk Scoring Logic</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                SALAR&rsquo;s public-facing explanation treats resilience as a measurable systems property.
                The exact implementation can vary by project, but the public logic is simple: combine design,
                failure, supply, manufacturing, and process-control risks &mdash; then adjust for evidence
                confidence.
              </p>
            </div>

            <div
              className="rounded-xl p-6 md:p-8 border border-cyan-500/30 mb-6"
              style={{ background: '#0A1628', boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }}
              data-testid="tsa-formula-panel"
            >
              <div className="space-y-5 font-mono text-sm md:text-base text-cyan-200 leading-relaxed">
                <div className="overflow-x-auto">
                  <div className="text-[10px] tracking-widest text-blue-300/80 mb-1 font-sans">TOTAL RESILIENCE RISK</div>
                  <code className="block whitespace-pre-wrap break-words text-white">
                    R_total = 0.20 &middot; R_design + 0.20 &middot; R_failure + 0.25 &middot; R_supply + 0.20 &middot; R_manufacturing + 0.15 &middot; R_process_control
                  </code>
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-blue-300/80 mb-1 font-sans">EVIDENCE COMPLETENESS</div>
                  <code className="block whitespace-pre-wrap text-white">
                    C_evidence = evidence_fields_present / evidence_fields_required
                  </code>
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-blue-300/80 mb-1 font-sans">EVIDENCE-ADJUSTED RISK</div>
                  <code className="block whitespace-pre-wrap text-white">
                    R_adjusted = min(1.00, R_total &middot; (1 + 0.15 &middot; (1 - C_evidence)))
                  </code>
                </div>
              </div>
              <p className="text-sm text-blue-100/90 mt-6 leading-relaxed border-t border-cyan-500/20 pt-5">
                <span className="font-bold text-cyan-300">Plain-English:</span> If evidence is weak, risk
                increases. If evidence is complete, the decision becomes more defensible.
              </p>
            </div>

            {/* Stylized supporting visuals (lightweight CSS — not real charts) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { t: 'Risk composition by lane',  hint: 'donut chart',         color: '#00D4FF' },
                { t: 'Component family vs. risk', hint: 'heatmap',             color: '#F59E0B' },
                { t: 'Evidence vs. risk reduction', hint: 'line chart',         color: '#A78BFA' },
                { t: 'Before / after mitigation', hint: 'stacked bar',         color: '#34D399' },
              ].map((v, i) => (
                <div
                  key={v.t}
                  className="rounded-lg p-4 border border-cyan-500/20"
                  style={{ background: '#1A2744' }}
                  data-testid={`tsa-risk-visual-${i}`}
                >
                  <div className="text-[10px] tracking-widest font-bold mb-2" style={{ color: v.color }}>
                    {v.hint.toUpperCase()}
                  </div>
                  <div className="text-xs text-white font-semibold mb-3 leading-snug">{v.t}</div>
                  {/* Visual abstraction */}
                  {i === 0 && (
                    <svg viewBox="0 0 60 60" className="w-full h-20" aria-hidden="true">
                      <circle cx="30" cy="30" r="20" fill="none" stroke="#1B6EC2" strokeOpacity="0.25" strokeWidth="8" />
                      <circle cx="30" cy="30" r="20" fill="none" stroke={v.color} strokeWidth="8" strokeDasharray="78 200" transform="rotate(-90 30 30)" />
                    </svg>
                  )}
                  {i === 1 && (
                    <div className="grid grid-cols-6 gap-0.5">
                      {[...Array(24)].map((_, k) => (
                        <div key={k} className="h-3 rounded-sm" style={{ background: v.color, opacity: 0.15 + ((k * 13) % 9) / 12 }} />
                      ))}
                    </div>
                  )}
                  {i === 2 && (
                    <svg viewBox="0 0 100 40" className="w-full h-20" aria-hidden="true">
                      <polyline fill="none" stroke={v.color} strokeWidth="2" points="0,32 20,28 40,22 60,16 80,10 100,7" />
                      <line x1="0" y1="38" x2="100" y2="38" stroke="#1B6EC2" strokeOpacity="0.3" />
                    </svg>
                  )}
                  {i === 3 && (
                    <div className="flex items-end gap-1 h-20">
                      {[55, 70, 60, 75, 50, 65].map((h, k) => (
                        <div key={k} className="flex-1 flex flex-col gap-0.5">
                          <div style={{ height: `${h * 0.6}%`, background: v.color, opacity: 0.85 }} className="rounded-t-sm" />
                          <div style={{ height: `${(100 - h) * 0.4}%`, background: '#F59E0B', opacity: 0.6 }} className="rounded-b-sm" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === SECTION 6 — BEST-FIT USE CASES === */}
      <section className="py-20" style={{ background: '#0A1628' }} data-testid="tsa-use-cases">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Section 06 &middot; Use Cases</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">Best-Fit Use Cases</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                The total solution architecture can support government, prime-contractor, semiconductor,
                packaging, distributed-AI, and commercial electronics teams that need better decision
                traceability.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
              {[
                { Icon: ShieldCheck, t: 'Defense and Government Sustainment',           d: 'Alternate-part evaluation, counterfeit/provenance review, depot sustainment, and mission electronics risk triage.' },
                { Icon: Award,       t: 'Prime Contractors and OEMs',                   d: 'Supplier risk review, board reliability governance, manufacturing-stability evidence, and qualification-readiness support.' },
                { Icon: Cpu,         t: 'Semiconductor and Packaging Ecosystems',       d: 'Process stability, package/component risk, OSAT visibility, yield-risk, and source-confidence reasoning.' },
                { Icon: Network,     t: 'Distributed AI and Edge Compute Infrastructure', d: 'GPU/server/power/cooling reliability, node commissioning evidence, fleet maintenance risk, and network-profile governance.' },
                { Icon: CircuitBoard,t: 'Commercial Electronics Reliability Teams',     d: 'Design review, sourcing risk, field-failure prioritization, and engineering-change decision support.' },
              ].map((u, i) => (
                <div
                  key={u.t}
                  data-testid={`tsa-usecase-${i}`}
                  className="rounded-xl p-5 border border-cyan-500/25 transition-transform hover:-translate-y-1 motion-reduce:hover:translate-y-0"
                  style={{ background: '#1A2744' }}
                >
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-3" style={{ background: 'rgba(0,212,255,0.10)', border: '1px solid rgba(0,212,255,0.4)' }}>
                    <u.Icon className="text-cyan-300" size={20} />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2 leading-snug">{u.t}</h3>
                  <p className="text-xs text-blue-100/90 leading-relaxed">{u.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === SECTION 7 — PATENT FOUNDATION === */}
      <section className="py-20" style={{ background: '#0F1A2B' }} data-testid="tsa-patents">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Section 07 &middot; Public-Safe IP</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">Patent-Aligned Public-Safe Foundation</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                SALAR&rsquo;s public patent portfolio supports the architecture without exposing sensitive
                implementation details.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
              {[
                { id: 'p1', t: 'P1 — Board Design Reliability Analysis Checklist Guidelines',                                              d: 'Filed / patent-pending reliability-governance lane for structured board review.' },
                { id: 'p2', t: 'P2 — Profile-Based Ethernet Connectivity for Multi-Lane SoC Devices',                                       d: 'Filed / patent-pending interconnect profile-governance lane for complex platforms.' },
                { id: 'p3', t: 'P3 — Graph-Based Multi-Tier Semiconductor Supply-Chain Risk Scoring and Closed-Loop Mitigation',            d: 'Filed / patent-pending lower-tier visibility and mitigation-scoring lane.' },
                { id: 'p4', t: 'P4 — Closed-Loop Manufacturing Stability Systems and Methods for Multi-Domain Control Production',         d: 'Filed / patent-pending process stability and closed-loop manufacturing-control lane.' },
                { id: 'p5', t: 'P5 — AI-Certified Lithography / Process Assurance',                                                         d: 'Public-safe high-level process-assurance positioning only.' },
              ].map((p) => (
                <div
                  key={p.id}
                  data-testid={`tsa-patent-${p.id}`}
                  className="rounded-xl p-5 border border-cyan-500/25"
                  style={{ background: '#1A2744', borderLeft: '3px solid #1B6EC2' }}
                >
                  <Badge variant="outline" className="border-cyan-400 text-cyan-200 mb-3 text-[10px] tracking-widest">
                    USPTO &middot; PENDING
                  </Badge>
                  <h3 className="text-sm font-bold text-white mb-2 leading-snug">{p.t}</h3>
                  <p className="text-xs text-blue-100/85 leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>

            <p className="text-xs text-blue-300/70 italic text-center mt-8 max-w-3xl mx-auto leading-relaxed">
              Internal patent claim language, draft claim charts, and internal master-book PDFs are not
              published on this page.
            </p>

            <div className="text-center mt-6">
              <Link to="/patent-portfolio">
                <Button variant="outline" className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]" data-testid="tsa-patents-link-btn">
                  View the public patent portfolio
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* === SECTION 8 — PUBLIC-SAFE GUARDRAIL === */}
      <section className="py-16" style={{ background: '#0A1628' }} data-testid="tsa-guardrail">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-xl p-7 border border-amber-400/30" style={{ background: 'linear-gradient(180deg, rgba(245,158,11,0.06), rgba(10,22,40,0.4))' }}>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/15 border border-amber-400/40 flex items-center justify-center">
                  <ShieldCheck size={20} className="text-amber-300" />
                </div>
                <div>
                  <SectionTag color="#FBBF24">Section 08 &middot; Public-Safe Guardrail</SectionTag>
                  <p className="text-sm md:text-base text-blue-100 leading-relaxed">
                    SALAR Technologies LLC provides independent engineering research, technical strategy, and
                    decision-support architecture for electronics reliability, semiconductor supply-chain
                    intelligence, provenance awareness, and manufacturing-stability analysis. Public materials
                    are informational and do not represent government certification, contract award, security
                    clearance, or endorsement by any agency, prime contractor, or technology vendor.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === INTERNAL LINKS === */}
      <section className="py-12" style={{ background: '#0F1A2B', borderTop: '1px solid rgba(96,165,250,0.15)' }} data-testid="tsa-internal-links">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <SectionTag>Explore More</SectionTag>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { to: '/salar-resilience-terrain', label: 'Explore the SALAR Resilience Terrain demo' },
                { to: '/patent-portfolio',         label: 'View the public patent portfolio' },
                { to: '/compliance',               label: 'Read public compliance statements' },
                { to: '/resources',                label: 'Request a capability statement' },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`tsa-internal-link-${l.to.replace(/\W+/g, '-').replace(/^-+/, '')}`}
                  className="block rounded-lg p-4 border border-cyan-500/25 text-cyan-100 hover:border-cyan-300 hover:text-white transition-colors"
                  style={{ background: '#1A2744' }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold">{l.label}</span>
                    <ArrowRight size={16} className="text-cyan-300 flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === HOW THE TOTAL SOLUTION ARCHITECTURE WORKS === */}
      <section className="py-20" style={{ background: '#0F1A2B' }} data-testid="tsa-how-it-works">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Architecture Walkthrough</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">How the Total Solution Architecture Works</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                Six layers turn fragmented signals into one explainable assurance workflow.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { id: 'board',      Icon: CircuitBoard, c: '#1B6EC2', t: 'Board Evidence',         d: 'Captures circuit-board layout, BOM, inspection, thermal, electrical, and design-rule evidence.' },
                { id: 'bom',        Icon: FileText,     c: '#22D3EE', t: 'BOM Evidence',           d: 'Turns bill-of-materials data into structured evidence for source, lifecycle, substitute, and risk review.' },
                { id: 'core',       Icon: Cpu,          c: '#A78BFA', t: 'SALAR Decision Core',    d: 'Combines reliability, provenance, supply-chain, manufacturing-stability, and predictive-failure signals into an explainable decision score.' },
                { id: 'heatmap',    Icon: Activity,     c: '#F59E0B', t: 'Risk Heatmap',           d: 'Shows which parts, suppliers, process steps, or operating conditions are driving the highest risk.' },
                { id: 'trust',      Icon: ShieldCheck,  c: '#34D399', t: 'Trusted-Zone Graph',     d: 'Maps source confidence, supplier relationships, qualification status, and trusted/untrusted pathways.' },
                { id: 'actions',    Icon: ListChecks,   c: '#FBBF24', t: 'Action Tiles',           d: 'Translate analysis into actions such as inspect, shield, sample, test, branch, map, report, or engineering review.' },
              ].map((x) => (
                <div
                  key={x.id}
                  data-testid={`tsa-howitworks-${x.id}`}
                  className="rounded-xl p-5 border transition-transform hover:-translate-y-1 motion-reduce:hover:translate-y-0"
                  style={{ background: '#1A2744', borderColor: `${x.c}55`, borderLeft: `3px solid ${x.c}` }}
                >
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-3" style={{ background: `${x.c}1a`, border: `1px solid ${x.c}55` }}>
                    <x.Icon size={20} style={{ color: x.c }} />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{x.t}</h3>
                  <p className="text-xs text-blue-100/90 leading-relaxed">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === P1–P5 ASSURANCE MODEL === */}
      <section className="py-20" style={{ background: '#0A1628' }} data-testid="tsa-p1p5-model">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Assurance Model</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">P1–P5 Assurance Model</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                The public-safe scoring model behind SALAR&rsquo;s decision support &mdash; five pillars,
                weighted, with evidence-completeness adjustment.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
              {[
                { id: 'P1', c: '#1B6EC2', t: 'Design Reliability / Board Evidence',           d: 'Checks whether the board design, layout, electrical margin, thermal path, and component choices support reliable operation.' },
                { id: 'P2', c: '#22D3EE', t: 'Evidence / Verification Readiness',              d: 'Checks whether test evidence, inspection records, logs, and validation artifacts are strong enough to support a decision.' },
                { id: 'P3', c: '#F59E0B', t: 'Supplier / Provenance Intelligence',             d: 'Checks source confidence, chain of custody, supplier relationships, and alternate-source exposure.' },
                { id: 'P4', c: '#FB923C', t: 'Manufacturing / Repairability Stability',        d: 'Checks whether process drift, repair workflow, substitutions, and production changes could create hidden instability.' },
                { id: 'P5', c: '#A78BFA', t: 'Qualification / Process Assurance',              d: 'Checks whether the final system can be qualified, defended, repeated, and trusted before operational use.' },
              ].map((p) => (
                <div
                  key={p.id}
                  data-testid={`tsa-p1p5-${p.id.toLowerCase()}`}
                  className="rounded-xl p-5 border"
                  style={{ background: '#1A2744', borderColor: `${p.c}55`, borderTop: `3px solid ${p.c}` }}
                >
                  <div className="text-xs font-bold tracking-widest" style={{ color: p.c }}>{p.id}</div>
                  <h3 className="text-base font-bold text-white mt-2 mb-2 leading-snug">{p.t}</h3>
                  <p className="text-xs text-blue-100/90 leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === WORKED ENGINEERING EXAMPLE === */}
      <section className="py-20" style={{ background: '#0F1A2B' }} data-testid="tsa-worked-example">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <SectionTag>Illustrative Scoring Model</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Worked Engineering Example</h2>
              <p className="text-sm text-blue-300 italic max-w-3xl mx-auto">
                Illustrative example only &mdash; not a live system score, government decision, or customer dataset.
              </p>
            </div>

            {/* Pillar dashboard cards with health/risk */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {[
                { id: 'P1', label: 'Design Reliability / Board Evidence',  H: 70, G: 30, w: 0.14, risk: 4.20, contrib: 10.52, c: '#1B6EC2' },
                { id: 'P2', label: 'Evidence / Verification Readiness',    H: 60, G: 40, w: 0.25, risk: 10.00, contrib: 25.04, c: '#22D3EE' },
                { id: 'P3', label: 'Supplier / Provenance Intelligence',   H: 65, G: 35, w: 0.19, risk: 6.65, contrib: 16.65, c: '#F59E0B' },
                { id: 'P4', label: 'Manufacturing / Repairability',         H: 62, G: 38, w: 0.16, risk: 6.08, contrib: 15.23, c: '#FB923C' },
                { id: 'P5', label: 'Qualification / Process Assurance',    H: 50, G: 50, w: 0.26, risk: 13.00, contrib: 32.56, c: '#A78BFA' },
              ].map((p) => (
                <div
                  key={p.id}
                  data-testid={`tsa-worked-${p.id.toLowerCase()}`}
                  className="rounded-xl p-4 border"
                  style={{ background: '#1A2744', borderColor: `${p.c}55`, borderLeft: `3px solid ${p.c}` }}
                >
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-xs font-bold tracking-widest" style={{ color: p.c }}>{p.id}</span>
                    <span className="text-[10px] tracking-widest text-blue-300/80">CONTRIB {p.contrib.toFixed(2)}%</span>
                  </div>
                  <div className="text-[11px] text-white font-semibold mb-3 leading-snug">{p.label}</div>
                  <div className="grid grid-cols-3 gap-1.5 text-[10px] text-blue-200 mb-3">
                    <div><div className="text-blue-400/70">Health H</div><div className="text-white font-bold text-sm">{p.H}</div></div>
                    <div><div className="text-blue-400/70">Gap G</div><div className="text-white font-bold text-sm">{p.G}</div></div>
                    <div><div className="text-blue-400/70">Weight w</div><div className="text-white font-bold text-sm">{p.w}</div></div>
                  </div>
                  {/* Contribution bar */}
                  <div className="h-1.5 rounded-full bg-blue-950/60 overflow-hidden" aria-hidden="true">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.min(100, p.contrib * 2)}%`, background: p.c }}
                    />
                  </div>
                  <div className="text-[10px] text-blue-200/85 mt-2">Risk points: <span className="text-white font-semibold">{p.risk.toFixed(2)}</span></div>
                </div>
              ))}
            </div>

            {/* Formula + totals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-xl p-6 border border-cyan-500/30" style={{ background: '#0A1628' }} data-testid="tsa-worked-formula">
                <div className="text-[10px] tracking-widest text-cyan-300 mb-2 font-bold">RISK SCORING FORMULA</div>
                <code className="block whitespace-pre-wrap text-white font-mono text-sm leading-relaxed mb-3">
                  Risk = &Sigma; (w&#x1d62; &middot; (100 &minus; H&#x1d62;))
                </code>
                <ul className="text-xs text-blue-100/90 leading-relaxed space-y-1 mt-4">
                  <li><span className="text-cyan-300 font-mono">H&#x1d62;</span> &mdash; health score for pillar i</li>
                  <li><span className="text-cyan-300 font-mono">100 &minus; H&#x1d62;</span> &mdash; gap or weakness</li>
                  <li><span className="text-cyan-300 font-mono">w&#x1d62;</span> &mdash; pillar weight</li>
                  <li>Risk points = how much that pillar contributes to total risk</li>
                </ul>
              </div>

              <div className="rounded-xl p-6 border border-amber-400/30" style={{ background: 'linear-gradient(180deg, rgba(245,158,11,0.08), rgba(10,22,40,0.4))' }} data-testid="tsa-worked-result">
                <div className="text-[10px] tracking-widest text-amber-300 mb-3 font-bold">EXAMPLE RESULT</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] text-blue-300/70 uppercase tracking-widest">Total Risk</div>
                    <div className="text-3xl font-bold text-amber-300">39.93 <span className="text-sm text-blue-300/80">/ 100</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] text-blue-300/70 uppercase tracking-widest">Health Total</div>
                    <div className="text-3xl font-bold text-cyan-300">60.07 <span className="text-sm text-blue-300/80">/ 100</span></div>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-amber-400/20 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] tracking-widest text-amber-300 font-bold w-32">TOP REPAIR TARGET</span>
                    <span className="text-white font-bold">P5 &mdash; Qualification / Process Assurance</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] tracking-widest text-amber-300 font-bold w-32">NEXT REPAIR</span>
                    <span className="text-white font-bold">P2 &mdash; Evidence / Verification Readiness</span>
                  </div>
                </div>
                <p className="text-xs text-blue-100/90 leading-relaxed mt-5 italic border-t border-amber-400/20 pt-4">
                  In this example, the system is not failing everywhere. The model shows that qualification /
                  process assurance and evidence / verification readiness are the two biggest weaknesses.
                  SALAR turns that into a repair path instead of a vague risk statement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === ODDA REPAIR LOOP === */}
      <section className="py-20" style={{ background: '#0A1628' }} data-testid="tsa-odda-loop">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Repair Loop</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Observe &rarr; Diagnose &rarr; Decide &rarr; Act</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                A closed-loop workflow that turns scoring into action, then re-scores to verify the system improved.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { id: 'observe',  step: 'OBSERVE',  c: '#1B6EC2', Icon: Eye,       t: 'Observe',  d: 'Collect design, BOM, inspection, source, test, and operating evidence.' },
                { id: 'diagnose', step: 'DIAGNOSE', c: '#22D3EE', Icon: Search,    t: 'Diagnose', d: 'Identify which P1–P5 pillars are weak and which evidence is missing.' },
                { id: 'decide',   step: 'DECIDE',   c: '#F59E0B', Icon: ListChecks,t: 'Decide',   d: 'Select repair path, inspection path, substitution path, qualification path, or source-control action.' },
                { id: 'act',      step: 'ACT',      c: '#34D399', Icon: Workflow,  t: 'Act',      d: 'Update evidence, re-score the system, and verify whether risk decreases.' },
              ].map((stage, i) => (
                <div key={stage.id} className="relative" data-testid={`tsa-odda-${stage.id}`}>
                  <div
                    className="rounded-xl p-5 border h-full"
                    style={{ background: '#1A2744', borderColor: `${stage.c}55`, borderTop: `3px solid ${stage.c}` }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stage.c}1a`, border: `1px solid ${stage.c}55` }}>
                        <stage.Icon size={18} style={{ color: stage.c }} />
                      </div>
                      <div>
                        <div className="text-[10px] font-bold tracking-widest" style={{ color: stage.c }}>STEP 0{i + 1}</div>
                        <div className="text-white font-bold">{stage.t}</div>
                      </div>
                    </div>
                    <p className="text-xs text-blue-100/90 leading-relaxed">{stage.d}</p>
                  </div>
                  {/* Connector arrow on desktop between cards */}
                  {i < 3 && (
                    <ArrowRight
                      size={20}
                      className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 text-cyan-400/60"
                      aria-hidden="true"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === WHAT THE ARCHITECTURE PRODUCES === */}
      <section className="py-20" style={{ background: '#0F1A2B' }} data-testid="tsa-produces">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <SectionTag>Artifacts</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">What the Architecture Produces</h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                Concrete outputs engineering, sourcing, sustainment, and leadership can review, share, and act on.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { id: 'memo',      Icon: FileText,    c: '#00D4FF', t: 'Decision Memo',      d: 'A readable explanation of what evidence drove the decision.' },
                { id: 'score',     Icon: Activity,    c: '#22D3EE', t: 'Risk Score',         d: 'A weighted score showing which pillar contributes the most risk.' },
                { id: 'repair',    Icon: ListChecks,  c: '#F59E0B', t: 'Repair Path',        d: 'A ranked action list showing what should be fixed first.' },
                { id: 'evidence',  Icon: Eye,         c: '#A78BFA', t: 'Evidence Trace',     d: 'A log of source, design, inspection, and verification data supporting the decision.' },
                { id: 'trust',     Icon: Network,     c: '#34D399', t: 'Trusted-Zone Map',   d: 'A supplier / source-confidence map showing trusted, weak, unknown, and risky paths.' },
                { id: 'readiness', Icon: ShieldCheck, c: '#FBBF24', t: 'Readiness View',     d: 'A compact view that helps engineers, primes, or agencies understand whether the system is ready, risky, or blocked.' },
              ].map((o) => (
                <Card
                  key={o.id}
                  data-testid={`tsa-produces-${o.id}`}
                  className="border transition-transform hover:-translate-y-1 motion-reduce:hover:translate-y-0"
                  style={{ background: '#1A2744', borderColor: `${o.c}40` }}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${o.c}18`, border: `1px solid ${o.c}60` }}>
                        <o.Icon size={18} style={{ color: o.c }} />
                      </div>
                      <h3 className="text-base font-bold text-white leading-snug">{o.t}</h3>
                    </div>
                    <p className="text-xs text-blue-100/90 leading-relaxed">{o.d}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === FOOTER CTA === */}
      <section
        className="py-20 text-white"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.85), rgba(10,22,40,0.95)), url('/img/salar_hero_semiconductor_resilience.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="tsa-footer-cta"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-5 leading-tight">
              Bring fragmented electronics evidence into one decision framework.
            </h2>
            <p className="text-base text-blue-100 leading-relaxed mb-8 max-w-3xl mx-auto">
              SALAR Technologies LLC helps convert board, supplier, provenance, manufacturing, and field
              evidence into structured, explainable technical decisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Link to="/contact" data-testid="tsa-footer-contact-btn">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
                  <Mail className="mr-2" size={18} />
                  Contact SALAR
                </Button>
              </Link>
              <a
                href="mailto:contact@salartechnologies.com?subject=SALAR%20Capability%20Statement%20Request"
                data-testid="tsa-footer-capstmt-btn"
              >
                <Button size="lg" variant="outline" className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]">
                  Request Capability Statement
                  <ExternalLink className="ml-2" size={16} />
                </Button>
              </a>
            </div>
            <p className="text-xs text-blue-300/85 tracking-wide" data-testid="tsa-footer-emails">
              <a href="mailto:contact@salartechnologies.com" className="hover:text-cyan-300 underline-offset-2 hover:underline">contact@salartechnologies.com</a>
              {' '}&middot;{' '}
              <a href="mailto:vikrant@salartechnologies.com" className="hover:text-cyan-300 underline-offset-2 hover:underline">vikrant@salartechnologies.com</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TotalSolutionArchitecture;
